// @flow

/**
 * Thread Download Actions Component
 * Renders download buttons for entire chat conversation
 */

import React, { cloneElement, isValidElement, useState } from 'react';
import styled from 'styled-components';
import { Button, Intent } from '@blueprintjs/core';
import { Tooltip2 } from '@blueprintjs/popover2';
import { useDownloadActions, usePromptChatState } from '../context';
import {
  combineMessagesForDownload,
  generateThreadFilename,
  hasDownloadableContent,
} from '../utils/threadDownloadUtils';
import { buildDownloadHeading, prependHeadingToMarkdown } from '../utils/downloadHeadings';
import { resolveDataDisplaysForExport } from '../utils/resolveDataDisplaysForExport';

import type { DownloadAction } from '../types';

// Icon size for top bar buttons (larger than message-level icons)
const TOP_BAR_ICON_SIZE = 20;

const ActionsContainer = styled.div`
  display: flex;
  gap: 0.25rem;
`;

type ThreadDownloadActionsProps = {
  /** Whether downloads are currently disabled (e.g., during streaming) */
  disabled?: boolean,
  /** Optional title to include in the exported heading (route-specific) */
  headingTitle?: string,
  /** Optional fallback title when headingTitle is missing */
  headingFallback?: string,
};

/**
 * Generates tooltip text for thread download
 * @param {string} actionId - Action ID (format identifier like 'docx', 'pdf')
 * @returns {string} Tooltip text
 */
const getThreadTooltip = (actionId: string): string => {
  const format = actionId.toUpperCase();
  return `Download this conversation as ${format}`;
};

/**
 * Resizes an icon element to the top bar size
 * @param {React$Node} icon - The icon element to resize
 * @returns {React$Node} Resized icon or original if not a valid element
 */
const resizeIcon = (icon: React$Node): React$Node => {
  if (isValidElement(icon)) {
    return cloneElement(icon, { width: TOP_BAR_ICON_SIZE, height: TOP_BAR_ICON_SIZE });
  }
  return icon;
};

const ThreadDownloadActions = ({
  disabled = false,
  headingTitle,
  headingFallback,
}: ThreadDownloadActionsProps) => {
  const downloadActions = useDownloadActions();
  const state = usePromptChatState();
  const [exporting, setExporting] = useState(false);

  // Don't render if no download actions configured
  if (!downloadActions || downloadActions.length === 0) {
    return null;
  }

  const { messages, isStreaming } = state;

  // Check if there's content to download (non-intro messages)
  const hasContent = hasDownloadableContent(messages);

  // Disable if no content, explicitly disabled, streaming, or currently exporting
  const isDisabled = !hasContent || disabled || isStreaming || exporting;

  const handleClick = async (action: DownloadAction, event: SyntheticEvent<HTMLButtonElement>) => {
    if (isDisabled) return;

    // Blur the button to remove focus/highlight
    if (event.currentTarget) {
      event.currentTarget.blur();
    }

    setExporting(true);
    try {
      // Collect all dataDisplays across messages
      const allDisplays = {};
      for (const msg of messages) {
        if (msg.dataDisplays) {
          Object.assign(allDisplays, msg.dataDisplays);
        }
      }

      const combinedMarkdown = combineMessagesForDownload(messages);

      // Resolve data displays (chart images + table conversions)
      const { markdown: resolvedMarkdown, chartImages } =
        await resolveDataDisplaysForExport(combinedMarkdown, allDisplays);

      const filename = generateThreadFilename(action.id);
      const heading = buildDownloadHeading({
        kind: 'thread',
        title: headingTitle,
        fallbackTitle: headingFallback,
      });
      const markdownWithHeading = prependHeadingToMarkdown(resolvedMarkdown, heading);

      await action.handler(markdownWithHeading, filename, chartImages);
    } catch (error) {
      console.error(`Thread download failed for ${action.id}:`, error);
    } finally {
      setExporting(false);
    }
  };

  return (
    <ActionsContainer>
      {downloadActions.map((action) => (
        <Tooltip2 
          key={action.id} 
          content={getThreadTooltip(action.id)} 
          position="bottom"
        >
          <Button
            icon={resizeIcon(action.icon)}
            intent={Intent.PRIMARY}
            disabled={isDisabled}
            onClick={(e) => handleClick(action, e)}
            aria-label={getThreadTooltip(action.id)}
          />
        </Tooltip2>
      ))}
    </ActionsContainer>
  );
};

export default ThreadDownloadActions;
